const CLAVE_SILENCIO = 'tibo.bandeja.silenciada';

/**
 * El aviso sonoro de la bandeja.
 *
 * Suena **sólo cuando entra una conversación nueva**, no con cada mensaje. Con
 * el volumen real de un tenant productivo —picos de 470 mensajes en diez
 * minutos— un sonido por mensaje se vuelve ruido de fondo y el operador lo
 * silencia el primer día. Avisar de lo que de verdad exige una decisión —hay
 * alguien nuevo— es lo que mantiene el aviso con significado.
 *
 * El tono se sintetiza con WebAudio en vez de cargar un archivo: son dos notas,
 * pesa cero en el bundle y no depende de que un asset se despliegue bien.
 */

let contexto: AudioContext | null = null;

function obtenerContexto(): AudioContext | null {
  if (contexto) return contexto;
  // Safari lo expone con prefijo; si no existe ninguno, el panel sigue andando mudo.
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  contexto = new Ctor();
  return contexto;
}

export function bandejaSilenciada(): boolean {
  try {
    return localStorage.getItem(CLAVE_SILENCIO) === '1';
  } catch {
    // Modo privado o storage bloqueado: se asume con sonido.
    return false;
  }
}

export function silenciarBandeja(silenciada: boolean): void {
  try {
    localStorage.setItem(CLAVE_SILENCIO, silenciada ? '1' : '0');
  } catch {
    // Si no se puede recordar, al menos vale para esta sesión.
  }
}

/**
 * Toca una secuencia de notas. Ignora la llamada si la bandeja está silenciada.
 *
 * Nunca lanza: un navegador que bloquea el audio hasta la primera interacción
 * rechaza el `resume()`, y eso no puede tumbar el render de la bandeja.
 */
function tocar(notas: number[], opciones: { separacion: number; largo: number; volumen: number }) {
  if (bandejaSilenciada()) return;
  try {
    const ctx = obtenerContexto();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();

    const ahora = ctx.currentTime;
    for (const [i, hz] of notas.entries()) {
      const osc = ctx.createOscillator();
      const vol = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;

      const inicio = ahora + i * opciones.separacion;
      const fin = inicio + opciones.largo;
      // Ataque y caída suaves: un tono cuadrado abrupto suena a error del sistema.
      vol.gain.setValueAtTime(0, inicio);
      vol.gain.linearRampToValueAtTime(opciones.volumen, inicio + 0.02);
      vol.gain.exponentialRampToValueAtTime(0.0001, fin);

      osc.connect(vol).connect(ctx.destination);
      osc.start(inicio);
      osc.stop(fin);
    }
  } catch {
    // El sonido es un extra; su falla no es visible ni bloqueante.
  }
}

/** Dos notas cortas, ascendentes: entró alguien nuevo. */
export function sonarConversacionNueva(): void {
  tocar([660, 880], { separacion: 0.11, largo: 0.16, volumen: 0.09 });
}

/**
 * Alguien necesita una persona (GKTIBO-97).
 *
 * **Tiene que distinguirse sin mirar la pantalla**, que es la decisión del PO
 * del 2026-09-22. Con el mismo sonido repetido habría que contar los pitidos.
 *
 * Tres notas **descendentes** contra las dos ascendentes del otro: una subida
 * se lee como «llegó algo», una bajada como «atendé esto». Y un poco más
 * lentas y más fuertes, porque compite con la atención puesta en otra cosa.
 *
 * No es un archivo de audio: se sintetiza igual que el otro, así que no pesa
 * en el bundle ni depende de que un asset se despliegue bien.
 */
export function sonarEscalacion(): void {
  tocar([880, 700, 560], { separacion: 0.15, largo: 0.2, volumen: 0.12 });
}
